import { DestroyRef, Injectable, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, finalize } from 'rxjs';
import { ConfigurationWaterfall, ConfigurationWaterfallService } from './configuration-waterfall.service';
import { Environment, RenderMode, editKind, identity, specNames } from './configuration-view';
import { HistoryCheckout } from './history-drawer/history-drawer';

@Injectable({ providedIn: 'root' })
export class WorkspaceStore {
  private readonly api = inject(ConfigurationWaterfallService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private lastFetch: 'saved' | 'cms' = 'saved';

  readonly rows = signal<ConfigurationWaterfall[]>([]);
  readonly loading = signal(false);
  readonly busyLabel = signal('status.loading');
  readonly loaded = signal(false);
  readonly error = signal<string | null>(null);
  readonly search = signal('');
  readonly selected = signal<ConfigurationWaterfall | null>(null);
  readonly environment = signal<Environment>('stable');
  readonly leftEnvironment = signal<Environment>('stable');
  readonly rightEnvironment = signal<Environment>('alfa');
  readonly renderMode = signal<RenderMode>('rendered');
  readonly editorOpen = signal(false);
  readonly historyTarget = signal<{ row: ConfigurationWaterfall; environment: Environment } | null>(null);
  readonly historyCheckout = signal<HistoryCheckout | null>(null);
  readonly crossEnvironment = signal<Environment>('stable');
  readonly leftSpec = signal<string | null>(null);
  readonly rightSpec = signal<string | null>(null);
  readonly crossSelection = signal<{ left: ConfigurationWaterfall; right: ConfigurationWaterfall } | null>(null);

  constructor() {
    effect(() => {
      const names = specNames(this.rows());
      const left = this.leftSpec();
      const right = this.rightSpec();
      if (names.length === 0) return;
      const nextLeft = left !== null && names.includes(left) ? left : names[0];
      const nextRight = right !== null && names.includes(right) && right !== nextLeft
        ? right
        : names.find(name => name !== nextLeft) ?? null;
      if (nextLeft !== left) this.leftSpec.set(nextLeft);
      if (nextRight !== right) this.rightSpec.set(nextRight);
      const selected = this.crossSelection();
      if (selected && (selected.left.swaggerName !== nextLeft || selected.right.swaggerName !== nextRight)) this.crossSelection.set(null);
    });
  }

  load(): void {
    this.fetch('saved');
  }

  refresh(): void {
    this.fetch('cms');
  }

  retry(): void {
    this.fetch(this.lastFetch);
  }

  showInCompare(row: ConfigurationWaterfall): void {
    this.selected.set(row);
    this.dismissOverlays();
    void this.router.navigate(['/compare']);
  }

  openEditor(): void {
    this.historyCheckout.set(null);
    this.closeHistory();
    this.editorOpen.set(true);
  }

  openCheckout(request: HistoryCheckout): void {
    this.historyCheckout.set(request);
    this.editorOpen.set(true);
  }

  closeEditor(): void {
    this.editorOpen.set(false);
    this.historyCheckout.set(null);
  }

  checkoutSource(): string | null {
    const checkout = this.historyCheckout();
    const history = this.historyTarget();
    if (!checkout || !history) return null;
    const value = editKind(history.row) === 'ApiCore' ? checkout.entry.apiCoreData : checkout.entry.applicationData;
    return value ?? '';
  }

  openHistory(request: { row: ConfigurationWaterfall; environment: Environment }): void {
    this.closeEditor();
    this.historyTarget.set(request);
  }

  closeHistory(): void {
    this.historyTarget.set(null);
  }

  dismissOverlays(): void {
    this.closeEditor();
    this.closeHistory();
  }

  editorTarget(): { left: ConfigurationWaterfall; right: ConfigurationWaterfall | null; leftEnvironment: Environment; rightEnvironment: Environment } | null {
    const checkout = this.historyCheckout();
    const history = this.historyTarget();
    if (checkout && history) {
      return { left: history.row, right: null, leftEnvironment: checkout.environment, rightEnvironment: checkout.environment };
    }
    if (this.router.url.split('?')[0].endsWith('/cross')) {
      const pair = this.crossSelection();
      if (!pair) return null;
      const environment = this.crossEnvironment();
      return { left: pair.left, right: pair.right, leftEnvironment: environment, rightEnvironment: environment };
    }
    const row = this.selected();
    if (!row) return null;
    return { left: row, right: null, leftEnvironment: this.leftEnvironment(), rightEnvironment: this.rightEnvironment() };
  }

  applySaved(updated: ConfigurationWaterfall): void {
    this.rows.update(list => list.map(item => identity(item) === identity(updated) ? updated : item));
    if (this.selected() && identity(this.selected()!) === identity(updated)) this.selected.set(updated);
    const history = this.historyTarget();
    if (history && identity(history.row) === identity(updated)) this.historyTarget.set({ ...history, row: updated });
    const cross = this.crossSelection();
    if (!cross) return;
    const left = identity(cross.left) === identity(updated) ? updated : cross.left;
    const right = identity(cross.right) === identity(updated) ? updated : cross.right;
    if (left !== cross.left || right !== cross.right) this.crossSelection.set({ left, right });
  }

  private fetch(source: 'saved' | 'cms'): void {
    if (this.loading()) return;
    this.lastFetch = source;
    this.busyLabel.set(source === 'cms' ? 'status.refreshing' : 'status.loading');
    this.loading.set(true);
    this.error.set(null);
    const request: Observable<ConfigurationWaterfall[]> = source === 'cms' ? this.api.refresh() : this.api.getAll();
    request.pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.loading.set(false))).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loaded.set(true);
        const selected = this.selected();
        if (selected) this.selected.set(rows.find(row => identity(row) === identity(selected)) ?? null);
        const cross = this.crossSelection();
        if (cross) {
          const left = rows.find(row => identity(row) === identity(cross.left));
          const right = rows.find(row => identity(row) === identity(cross.right));
          this.crossSelection.set(left && right ? { left, right } : null);
        }
      },
      error: () => this.error.set(source === 'cms' ? 'errors.refresh' : 'errors.load'),
    });
  }
}
